import { fetchCategoryLocaleData, fetchCategorySharedData } from './dataApi';
import { buildBasicSections } from './basicLearning';

// basic-phrases-sentences has no target-vs-native field asymmetry: an
// item's {id, key, category, text} is needed identically whether it's
// playing the "phrase being learned" role or the "reader's own language"
// role (both of which, like vocabulary and conversation, can be any of the
// ~40 supported codes, not just the 4 curriculum homes) — so every code's
// data-common/basic-phrases-sentences/{code}/index.json (see dataApi.js's
// categoryLocaleUrl) already holds the full item shape, not a reduced one,
// and resolves the same way regardless of whether it happens to be a
// curriculum home. The physical file used to live duplicated under every
// home's own basic-phrases-sentences/, one full copy per home even for
// codes that aren't that home's own language.
//
// See unwrapItem below for the key-indexed (rather than array) shape each
// index.json is actually stored in.
//
// Pronunciation is a third axis of its own: a transliteration of one
// locale's phrases into another locale's script/reading conventions, so
// it's inherently keyed by a (targetCode, readerCode) pair, and genuinely
// per-target-home (the sounds being transliterated are the target
// language's own phrases) — pronunciation.json lives alongside a home's own
// index.json and holds every reader's transliteration of that one target
// locale's phrases, keyed by reader code.
const itemsPromises = new Map();
const pronunciationPromises = new Map();

function fetchItems(code) {
  if (!itemsPromises.has(code)) {
    itemsPromises.set(
      code,
      fetchCategoryLocaleData('basic-phrases-sentences', code).catch((error) => {
        itemsPromises.delete(code);
        throw error;
      }),
    );
  }
  return itemsPromises.get(code);
}

function fetchPronunciationDoc(targetCode) {
  if (!pronunciationPromises.has(targetCode)) {
    pronunciationPromises.set(
      targetCode,
      fetchCategoryLocaleData('basic-phrases-sentences', targetCode, 'pronunciation.json').catch(() => ({})),
    );
  }
  return pronunciationPromises.get(targetCode);
}

// category/text are authored as { txt, translate, translationHint } (same
// convention as topic-lessons/exam/quick-sheet), so unwrap once here rather
// than touching every call site — mirrors unwrapExamQuestion.
function unwrapText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt ?? null : value;
}

// Each index.json is key-indexed — { hello: {id, category, text,
// translationHint}, sorry: {...}, ... } — rather than an array, so looking
// a phrase up by its stable key is O(1) instead of a linear scan;
// unwrapItem re-injects `key` from the object key each item is stored
// under, since every existing call site (buildBasicSections included)
// expects it on the item itself.
function unwrapItem(key, item) {
  return { ...item, key, category: unwrapText(item.category), text: unwrapText(item.text) };
}

async function resolveItems(code) {
  const doc = await fetchItems(code);
  return Object.entries(doc).map(([key, item]) => unwrapItem(key, item));
}

export async function loadBasicPhraseSections(homeLang, targetCode, nativeCode) {
  const [targetItems, nativeItems, pronunciationDoc] = await Promise.all([
    resolveItems(targetCode),
    resolveItems(nativeCode),
    fetchPronunciationDoc(targetCode),
  ]);
  const pronunciations = pronunciationDoc[nativeCode] || {};
  return buildBasicSections(targetItems, nativeItems, pronunciations);
}

// Phrasebook: all ~80 phrase keys, one language at a time (category-grouped
// browse view) — reuses the same per-locale index.json as loadBasicPhraseSections
// above, just without the target/native pairing.
export async function loadPhrasebookLanguageItems(code) {
  return resolveItems(code);
}

const allPromise = { current: null };

// Phrasebook catalog: the precomputed cross-language file holding every
// phrase key's category/text rendered in all ~40 locale codes at once, so
// the catalog screen can show every phrase + multi-language previews from
// one request instead of one per language. Shape: { [key]: { id, category:
// { [code]: text }, text: { [code]: text } } } — already flat, no per-locale
// unwrapping needed since each leaf is a plain string, not a {txt,...} node.
export function loadPhrasebookAll({ force = false } = {}) {
  if (force) allPromise.current = null;
  if (!allPromise.current) {
    allPromise.current = fetchCategorySharedData('basic-phrases-sentences', '_all.json', { force }).catch((error) => {
      allPromise.current = null;
      throw error;
    });
  }
  return allPromise.current;
}

export function clearBasicPhrasesV2Cache() {
  itemsPromises.clear();
  pronunciationPromises.clear();
  allPromise.current = null;
}
